import { Fragment, useEffect, useMemo, useState } from "react";
import {
  assignConfirmingBoeAccount,
  cancelConfirmingBatchApi,
  clearConfirmingBoeAccount,
  createConfirmingBatch,
  deleteConfirmingBatchApi,
  confirmingBatchFileUrl,
  fetchAccounts,
  fetchCompanies,
  fetchConfirmingBatches,
  fetchConfirmingDuesCalendar,
  fetchOpenSupplierInvoicesForConfirming,
  postConfirmingBatch,
  settleConfirmingBatch,
  settleConfirmingByKeys,
  type Account,
  type Company,
  type ConfirmingBatch,
  type ConfirmingBatchLine,
  type ConfirmingDueGroup,
  type ConfirmingDueLine,
  type SupplierOpenInvoiceRow,
} from "../api";

const SIN_METODO = "(sin método)";

type ColId =
  | "check"
  | "provider"
  | "docNum"
  | "ref"
  | "docDate"
  | "invoiceDueDate"
  | "payMethod"
  | "total"
  | "pending"
  | "toPay"
  | "effectDueDate"
  | "concept";

type ColDef = { id: ColId; label: string; defaultWidth: number; alignRight?: boolean };

const INVOICE_COLUMNS: ColDef[] = [
  { id: "check", label: "", defaultWidth: 32 },
  { id: "provider", label: "Proveedor", defaultWidth: 260 },
  { id: "docNum", label: "Nº fra SAP", defaultWidth: 90 },
  { id: "ref", label: "Ref. proveedor", defaultWidth: 120 },
  { id: "docDate", label: "F. factura", defaultWidth: 100 },
  { id: "invoiceDueDate", label: "Venc. factura", defaultWidth: 110 },
  { id: "payMethod", label: "Método pago", defaultWidth: 130 },
  { id: "total", label: "Total", defaultWidth: 100, alignRight: true },
  { id: "pending", label: "Pendiente", defaultWidth: 110, alignRight: true },
  { id: "toPay", label: "A pagar", defaultWidth: 110, alignRight: true },
  { id: "effectDueDate", label: "Venc. efecto", defaultWidth: 140 },
  { id: "concept", label: "Concepto", defaultWidth: 200 },
];

const INVOICE_COLS_STORAGE_KEY = "confirmingBatches.invoiceColumnWidths.v1";

function loadColumnWidths(): Record<ColId, number> {
  const defaults: Record<string, number> = Object.fromEntries(
    INVOICE_COLUMNS.map((c) => [c.id, c.defaultWidth]),
  );
  try {
    const raw = localStorage.getItem(INVOICE_COLS_STORAGE_KEY);
    if (raw) {
      const parsed = JSON.parse(raw) as Record<string, unknown>;
      for (const [k, v] of Object.entries(parsed)) {
        if (typeof v === "number" && v >= 32 && v <= 800) defaults[k] = v;
      }
    }
  } catch {
    /* ignora */
  }
  return defaults as Record<ColId, number>;
}

function ResizableTh({
  width,
  onResize,
  align,
  children,
}: {
  width: number;
  onResize: (newWidth: number) => void;
  align?: "left" | "right";
  children: React.ReactNode;
}): JSX.Element {
  const onMouseDown = (e: React.MouseEvent) => {
    e.preventDefault();
    const startX = e.clientX;
    const startWidth = width;
    const onMove = (ev: MouseEvent) => {
      const next = Math.max(40, Math.min(800, startWidth + (ev.clientX - startX)));
      onResize(next);
    };
    const onUp = () => {
      window.removeEventListener("mousemove", onMove);
      window.removeEventListener("mouseup", onUp);
      document.body.style.cursor = "";
      document.body.style.userSelect = "";
    };
    document.body.style.cursor = "col-resize";
    document.body.style.userSelect = "none";
    window.addEventListener("mousemove", onMove);
    window.addEventListener("mouseup", onUp);
  };
  return (
    <th style={{ position: "relative", textAlign: align === "right" ? "right" : "left" }}>
      {children}
      <div
        onMouseDown={onMouseDown}
        title="Arrastra para cambiar el ancho de la columna"
        style={{
          position: "absolute",
          top: 0,
          right: 0,
          bottom: 0,
          width: 6,
          cursor: "col-resize",
          userSelect: "none",
        }}
      />
    </th>
  );
}

/** +N días sobre hoy (YYYY-MM-DD). N=60 por defecto: vencimiento típico
 *  del confirming a 60 días si el usuario no ajusta nada. */
function addDaysIso(days: number): string {
  const d = new Date();
  d.setDate(d.getDate() + days);
  return d.toISOString().slice(0, 10);
}

/** Tabs de la vista Confirming. Persisten en localStorage para que la
 *  navegación vuelva al mismo tab tras un refresh. */
type ConfirmingTab = "nueva" | "pendientes" | "calendario";
const TAB_STORAGE_KEY = "confirmingBatches.activeTab.v1";

/** Definición de tabs con etiqueta y descripción para la barra. */
const TABS: Array<{ id: ConfirmingTab; label: string; hint: string }> = [
  { id: "nueva", label: "Nueva remesa", hint: "Cargar partidas abiertas y generar fichero SEPA/CSB68/XLS" },
  { id: "pendientes", label: "Ficheros pendientes", hint: "Contabilizar los efectos de los ficheros generados" },
  { id: "calendario", label: "Calendario de pagos", hint: "Vencimientos agregados de todos los efectos abiertos en OBOE" },
];

export function ConfirmingBatches(): JSX.Element {
  useEffect(() => {
    document.body.classList.add("main-wide");
    return () => document.body.classList.remove("main-wide");
  }, []);

  const [activeTab, setActiveTab] = useState<ConfirmingTab>(() => {
    try {
      const raw = localStorage.getItem(TAB_STORAGE_KEY);
      if (raw === "nueva" || raw === "pendientes" || raw === "calendario") return raw;
    } catch { /* ignora */ }
    return "nueva";
  });
  useEffect(() => {
    try { localStorage.setItem(TAB_STORAGE_KEY, activeTab); } catch { /* ignora */ }
  }, [activeTab]);

  const [companies, setCompanies] = useState<Company[]>([]);
  const [accounts, setAccounts] = useState<Account[]>([]);
  const [selectedCompany, setSelectedCompany] = useState<string>("");
  const [selectedIban, setSelectedIban] = useState<string>("");
  const [invoices, setInvoices] = useState<SupplierOpenInvoiceRow[] | null>(null);
  const [batches, setBatches] = useState<ConfirmingBatch[]>([]);
  const [selection, setSelection] = useState<
    Map<number, { amount: number; remittance: string; dueDate: string }>
  >(new Map());
  /** Fecha por defecto para las nuevas selecciones — se propaga a las
   *  facturas que marques a partir de ahora. NO cambia las ya elegidas. */
  const [defaultDueDate, setDefaultDueDate] = useState<string>(() => addDaysIso(60));
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [notice, setNotice] = useState<string | null>(null);

  const [activePayMethods, setActivePayMethods] = useState<Set<string>>(new Set());
  const [columnWidths, setColumnWidths] = useState<Record<ColId, number>>(() => loadColumnWidths());
  useEffect(() => {
    try {
      localStorage.setItem(INVOICE_COLS_STORAGE_KEY, JSON.stringify(columnWidths));
    } catch {
      /* ignora */
    }
  }, [columnWidths]);
  const setColWidth = (id: ColId, w: number) => setColumnWidths((prev) => ({ ...prev, [id]: w }));
  const resetColWidths = () =>
    setColumnWidths(
      Object.fromEntries(INVOICE_COLUMNS.map((c) => [c.id, c.defaultWidth])) as Record<ColId, number>,
    );

  useEffect(() => {
    (async () => {
      try {
        const [c, a] = await Promise.all([fetchCompanies(), fetchAccounts()]);
        const withSap = c.items.filter((x) => x.sap);
        setCompanies(withSap);
        setAccounts(a.items);
        if (withSap.length > 0 && !selectedCompany) setSelectedCompany(withSap[0]!.id);
      } catch (e: unknown) {
        setError(e instanceof Error ? e.message : String(e));
      }
    })();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const companyAccounts = useMemo(
    () => accounts.filter((a) => a.companyId === selectedCompany && a.active),
    [accounts, selectedCompany],
  );

  useEffect(() => {
    setInvoices(null);
    setSelection(new Map());
    setSelectedIban("");
    setError(null);
    setNotice(null);
    if (selectedCompany) void reloadBatches();
    else setBatches([]);
    const firstWithGL = companyAccounts.find((a) => a.sapGLAccount);
    if (firstWithGL) setSelectedIban(firstWithGL.iban);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedCompany]);

  const reloadBatches = async () => {
    if (!selectedCompany) return;
    try {
      const r = await fetchConfirmingBatches(selectedCompany);
      setBatches(r.items);
    } catch (e: unknown) {
      setError(e instanceof Error ? e.message : String(e));
    }
  };

  const loadInvoices = async () => {
    if (!selectedCompany) return;
    setBusy(true);
    setError(null);
    setNotice(null);
    try {
      const r = await fetchOpenSupplierInvoicesForConfirming(selectedCompany);
      setInvoices(r.items);
      setSelection(new Map());
      const initial = new Set<string>();
      for (const inv of r.items) {
        const m = inv.PayMethod || SIN_METODO;
        if (!/domicil/i.test(m)) initial.add(m);
      }
      setActivePayMethods(initial);
    } catch (e: unknown) {
      setError(e instanceof Error ? e.message : String(e));
    } finally {
      setBusy(false);
    }
  };

  const payMethodStats = useMemo(() => {
    if (!invoices) return [] as Array<{ method: string; count: number }>;
    const counts = new Map<string, number>();
    for (const r of invoices) {
      const m = r.PayMethod || SIN_METODO;
      counts.set(m, (counts.get(m) ?? 0) + 1);
    }
    return Array.from(counts.entries())
      .sort((a, b) => a[0].localeCompare(b[0], "es"))
      .map(([method, count]) => ({ method, count }));
  }, [invoices]);

  const visibleInvoices = useMemo(() => {
    if (!invoices) return null;
    return invoices.filter((r) => activePayMethods.has(r.PayMethod || SIN_METODO));
  }, [invoices, activePayMethods]);

  const togglePayMethod = (method: string) => {
    const next = new Set(activePayMethods);
    if (next.has(method)) {
      next.delete(method);
      if (invoices) {
        const nextSel = new Map(selection);
        for (const r of invoices) {
          if ((r.PayMethod || SIN_METODO) === method) nextSel.delete(r.DocEntry);
        }
        setSelection(nextSel);
      }
    } else {
      next.add(method);
    }
    setActivePayMethods(next);
  };

  const setAllPayMethodsActive = (active: boolean) => {
    if (active) {
      setActivePayMethods(new Set(payMethodStats.map((m) => m.method)));
    } else {
      setActivePayMethods(new Set());
      setSelection(new Map());
    }
  };

  const toggleInvoice = (row: SupplierOpenInvoiceRow) => {
    if (!row.BpIban) return;
    const next = new Map(selection);
    if (next.has(row.DocEntry)) {
      next.delete(row.DocEntry);
    } else {
      next.set(row.DocEntry, {
        amount: row.PendingAmount,
        remittance: `Fra ${row.NumAtCard || row.DocNum}`,
        dueDate: defaultDueDate,
      });
    }
    setSelection(next);
  };

  const updateSelection = (
    docEntry: number,
    patch: Partial<{ amount: number; remittance: string; dueDate: string }>,
  ) => {
    const cur = selection.get(docEntry);
    if (!cur) return;
    const next = new Map(selection);
    next.set(docEntry, { ...cur, ...patch });
    setSelection(next);
  };

  const toggleSelectAllPayable = () => {
    if (!visibleInvoices) return;
    const payable = visibleInvoices.filter((r) => r.BpIban);
    if (payable.length > 0 && payable.every((r) => selection.has(r.DocEntry))) {
      const next = new Map(selection);
      for (const r of payable) next.delete(r.DocEntry);
      setSelection(next);
    } else {
      const next = new Map(selection);
      for (const r of payable) {
        if (next.has(r.DocEntry)) continue;
        next.set(r.DocEntry, {
          amount: r.PendingAmount,
          remittance: `Fra ${r.NumAtCard || r.DocNum}`,
          dueDate: defaultDueDate,
        });
      }
      setSelection(next);
    }
  };

  const totalSelected = useMemo(() => {
    let t = 0;
    for (const [, v] of selection) t += Number(v.amount) || 0;
    return Math.round(t * 100) / 100;
  }, [selection]);

  const generateBatch = async () => {
    if (!selectedCompany || !selectedIban || selection.size === 0) return;
    const account = companyAccounts.find((a) => a.iban === selectedIban);
    if (!account?.sapGLAccount) {
      setError(`La cuenta ${selectedIban} no tiene G/L SAP. Rellénalo en Cuentas.`);
      return;
    }
    setBusy(true);
    setError(null);
    setNotice(null);
    try {
      const lines = Array.from(selection.entries()).map(([docEntry, v]) => ({
        invoiceDocEntry: docEntry,
        amount: v.amount,
        dueDate: v.dueDate,
        remittanceInfo: v.remittance,
      }));
      const r = await createConfirmingBatch({
        companyId: selectedCompany,
        senderIban: selectedIban,
        lines,
      });
      setNotice(
        `✓ Fichero confirming generado: ${r.batch.filename} · ${r.batch.lines.length} efectos · total ${eur(r.batch.totalAmount)}`,
      );
      window.open(confirmingBatchFileUrl(r.batch.id), "_blank");
      await Promise.all([loadInvoices(), reloadBatches()]);
    } catch (e: unknown) {
      setError(e instanceof Error ? e.message : String(e));
    } finally {
      setBusy(false);
    }
  };

  const groupedByCard = useMemo(() => {
    if (!visibleInvoices) return [] as Array<{ cardCode: string; cardName: string; bpIban: string; rows: SupplierOpenInvoiceRow[] }>;
    const map = new Map<string, { cardCode: string; cardName: string; bpIban: string; rows: SupplierOpenInvoiceRow[] }>();
    for (const r of visibleInvoices) {
      const cur = map.get(r.CardCode);
      if (cur) cur.rows.push(r);
      else map.set(r.CardCode, { cardCode: r.CardCode, cardName: r.CardName, bpIban: r.BpIban, rows: [r] });
    }
    return Array.from(map.values()).sort((a, b) => {
      if (!a.bpIban && b.bpIban) return 1;
      if (a.bpIban && !b.bpIban) return -1;
      return a.cardName.localeCompare(b.cardName, "es");
    });
  }, [visibleInvoices]);

  return (
    <>
      {/* Header sticky con título, selector empresa (común a todos los
          tabs) y navegación entre tabs. Compacto y siempre visible. */}
      <section
        className="card"
        style={{
          position: "sticky",
          top: 0,
          zIndex: 10,
          padding: "10px 14px",
          marginBottom: 12,
        }}
      >
        <div
          style={{
            display: "flex",
            gap: 16,
            alignItems: "center",
            flexWrap: "wrap",
            justifyContent: "space-between",
          }}
        >
          <div style={{ display: "flex", alignItems: "center", gap: 12 }}>
            <h2 style={{ margin: 0, fontSize: "1rem" }}>Confirming</h2>
            <label style={{ display: "flex", alignItems: "center", gap: 6, fontSize: "0.9em" }}>
              <span style={{ color: "#666" }}>Empresa:</span>
              <select
                value={selectedCompany}
                onChange={(e) => setSelectedCompany(e.target.value)}
                disabled={busy || companies.length === 0}
              >
                {companies.length === 0 && <option>Sin empresas SAP</option>}
                {companies.map((c) => (
                  <option key={c.id} value={c.id}>{c.alias || c.name}</option>
                ))}
              </select>
            </label>
          </div>
          <nav
            style={{
              display: "flex",
              gap: 4,
              background: "rgba(120,150,200,0.08)",
              borderRadius: 8,
              padding: 3,
            }}
          >
            {TABS.map((t) => {
              const active = activeTab === t.id;
              return (
                <button
                  key={t.id}
                  type="button"
                  onClick={() => setActiveTab(t.id)}
                  title={t.hint}
                  style={{
                    padding: "6px 14px",
                    borderRadius: 6,
                    fontSize: "0.9em",
                    fontWeight: active ? 600 : 400,
                    border: "none",
                    background: active ? "#fff" : "transparent",
                    color: active ? "#2c5aa0" : "#555",
                    boxShadow: active ? "0 1px 3px rgba(0,0,0,0.08)" : "none",
                    cursor: "pointer",
                    transition: "all 0.12s ease",
                  }}
                >
                  {t.label}
                </button>
              );
            })}
          </nav>
        </div>
      </section>

      {activeTab === "nueva" && (
      <section className="card" style={{ padding: "12px 14px" }}>
        <div className="card-head" style={{ marginBottom: 8 }}>
          <div>
            <h3 style={{ margin: 0, fontSize: "0.95rem" }}>Nueva remesa</h3>
            <p className="meta" style={{ margin: "4px 0 0 0", fontSize: "0.85em" }}>
              Marca las facturas a incluir en el fichero, ajusta la fecha de vencimiento del
              efecto y genera. Al subir el XLS al portal Santander, marca "He utilizado
              plantilla Excel".
            </p>
          </div>
          <div style={{ display: "flex", gap: 8, alignItems: "center", flexWrap: "wrap" }}>
            <label style={{ fontSize: "0.9em" }}>
              Cta emisora:{" "}
              <select
                value={selectedIban}
                onChange={(e) => setSelectedIban(e.target.value)}
                disabled={busy || companyAccounts.length === 0}
              >
                <option value="">— selecciona —</option>
                {companyAccounts.map((a) => (
                  <option key={a.iban} value={a.iban} disabled={!a.sapGLAccount}>
                    {a.alias || a.iban} ({a.iban})
                    {a.sapGLAccount ? ` · G/L ${a.sapGLAccount}` : " · SIN G/L SAP"}
                  </option>
                ))}
              </select>
            </label>
            <label style={{ fontSize: "0.9em" }}>
              Venc. default:{" "}
              <input
                type="date"
                value={defaultDueDate}
                onChange={(e) => setDefaultDueDate(e.target.value)}
                disabled={busy}
              />
            </label>
            <button type="button" onClick={loadInvoices} disabled={busy || !selectedCompany}>
              {busy ? "Cargando…" : "↻ Cargar facturas"}
            </button>
          </div>
        </div>

        {error && <p className="error">{error}</p>}
        {notice && (
          <pre
            style={{
              whiteSpace: "pre-wrap",
              background: "rgba(80,200,120,0.08)",
              padding: 8,
              borderRadius: 4,
              margin: "8px 0",
            }}
          >
            {notice}
          </pre>
        )}

        {invoices !== null && invoices.length === 0 && (
          <p className="meta">✓ No hay facturas de proveedor abiertas para esta empresa.</p>
        )}

        {invoices && invoices.length > 0 && (
          <>
            {payMethodStats.length > 1 && (
              <div
                style={{
                  display: "flex",
                  gap: 6,
                  alignItems: "center",
                  margin: "12px 0 4px 0",
                  flexWrap: "wrap",
                }}
              >
                <span className="meta" style={{ marginRight: 4 }}>Método pago:</span>
                {payMethodStats.map(({ method, count }) => {
                  const active = activePayMethods.has(method);
                  return (
                    <button
                      key={method}
                      type="button"
                      onClick={() => togglePayMethod(method)}
                      disabled={busy}
                      style={{
                        padding: "2px 10px",
                        borderRadius: 12,
                        fontSize: "0.85em",
                        border: `1px solid ${active ? "#4a8" : "#ccc"}`,
                        background: active ? "rgba(80,180,120,0.15)" : "rgba(150,150,150,0.08)",
                        color: active ? "#282" : "#888",
                        cursor: "pointer",
                        textDecoration: active ? "none" : "line-through",
                      }}
                    >
                      {method} ({count})
                    </button>
                  );
                })}
                <button
                  type="button"
                  onClick={() => setAllPayMethodsActive(true)}
                  disabled={busy}
                  style={{ fontSize: "0.8em", padding: "2px 8px" }}
                >
                  Todos
                </button>
                <button
                  type="button"
                  onClick={() => setAllPayMethodsActive(false)}
                  disabled={busy}
                  style={{ fontSize: "0.8em", padding: "2px 8px" }}
                >
                  Ninguno
                </button>
              </div>
            )}
            <div style={{ display: "flex", gap: 8, alignItems: "center", margin: "12px 0", flexWrap: "wrap" }}>
              <button
                type="button"
                onClick={toggleSelectAllPayable}
                disabled={busy || !visibleInvoices || visibleInvoices.length === 0}
              >
                {selection.size > 0 ? "Deseleccionar" : "Seleccionar todas (con IBAN)"}
              </button>
              <span className="meta">
                {selection.size} seleccionada(s) · Total <strong>{eur(totalSelected)}</strong>
                {visibleInvoices && invoices && visibleInvoices.length !== invoices.length && (
                  <> · Mostrando {visibleInvoices.length} de {invoices.length} facturas</>
                )}
              </span>
              <div style={{ marginLeft: "auto", display: "flex", gap: 8, alignItems: "center" }}>
                <button
                  type="button"
                  onClick={resetColWidths}
                  disabled={busy}
                  style={{ fontSize: "0.85em" }}
                >
                  ↔ Restaurar anchos
                </button>
                <button
                  type="button"
                  onClick={generateBatch}
                  disabled={
                    busy ||
                    selection.size === 0 ||
                    !selectedIban ||
                    !companyAccounts.find((a) => a.iban === selectedIban)?.sapGLAccount
                  }
                >
                  📄 Generar fichero confirming ({selection.size})
                </button>
              </div>
            </div>

            {visibleInvoices && visibleInvoices.length === 0 && (
              <p
                className="meta"
                style={{
                  margin: "12px 0",
                  padding: 8,
                  background: "rgba(255,180,50,0.10)",
                  borderRadius: 4,
                }}
              >
                No hay facturas visibles con los métodos actualmente activos. Activa algún chip.
              </p>
            )}

            <div style={{ overflowX: "auto", width: "100%" }}>
              <table
                className="data compact"
                style={{ whiteSpace: "nowrap", width: "100%", tableLayout: "fixed" }}
              >
                <colgroup>
                  {INVOICE_COLUMNS.map((col) => (
                    <col key={col.id} style={{ width: `${columnWidths[col.id]}px` }} />
                  ))}
                </colgroup>
                <thead>
                  <tr>
                    {INVOICE_COLUMNS.map((col) => (
                      <ResizableTh
                        key={col.id}
                        width={columnWidths[col.id]}
                        onResize={(w) => setColWidth(col.id, w)}
                        align={col.alignRight ? "right" : "left"}
                      >
                        {col.label}
                      </ResizableTh>
                    ))}
                  </tr>
                </thead>
                <tbody>
                  {groupedByCard.map((g) =>
                    g.rows.map((r, idx) => {
                      const sel = selection.get(r.DocEntry);
                      const hasIban = Boolean(r.BpIban);
                      const isFirst = idx === 0;
                      const cellStyle: React.CSSProperties = {
                        overflow: "hidden",
                        textOverflow: "ellipsis",
                      };
                      const rowTitle = hasIban
                        ? `IBAN acreedor: ${formatIban(r.BpIban)}`
                        : "El proveedor NO tiene IBAN configurado en SAP.";
                      return (
                        <tr
                          key={r.DocEntry}
                          title={rowTitle}
                          style={{
                            opacity: hasIban ? 1 : 0.6,
                            borderTop: isFirst ? "1px solid rgba(120,150,200,0.25)" : undefined,
                          }}
                        >
                          <td style={cellStyle}>
                            <input
                              type="checkbox"
                              checked={Boolean(sel)}
                              disabled={!hasIban || busy}
                              onChange={() => toggleInvoice(r)}
                            />
                          </td>
                          <td style={cellStyle}>
                            {isFirst ? (
                              <>
                                <span style={{ fontWeight: 600 }}>{r.CardName}</span>{" "}
                                <span style={{ fontSize: "0.85em", color: "#888" }}>({r.CardCode})</span>
                                {!hasIban && (
                                  <span
                                    style={{
                                      marginLeft: 8,
                                      color: "#c33",
                                      fontSize: "0.85em",
                                      fontWeight: 600,
                                    }}
                                  >
                                    ⚠ Sin IBAN
                                  </span>
                                )}
                              </>
                            ) : (
                              <span style={{ color: "#aaa" }}>↳ misma</span>
                            )}
                          </td>
                          <td style={cellStyle}>{r.DocNum}</td>
                          <td style={cellStyle}>{r.NumAtCard || <span style={{ color: "#aaa" }}>—</span>}</td>
                          <td style={cellStyle}>{r.DocDate}</td>
                          <td style={cellStyle}>{r.DocDueDate}</td>
                          <td style={{ ...cellStyle, fontSize: "0.85em", color: "#666" }}>
                            {r.PayMethod || <span style={{ color: "#aaa" }}>—</span>}
                          </td>
                          <td style={{ ...cellStyle, textAlign: "right" }}>{eur(r.DocTotal)}</td>
                          <td style={{ ...cellStyle, textAlign: "right" }}>
                            <strong>{eur(r.PendingAmount)}</strong>
                          </td>
                          <td style={{ ...cellStyle, textAlign: "right" }}>
                            {sel ? (
                              <input
                                type="number"
                                step="0.01"
                                min="0.01"
                                max={r.PendingAmount}
                                value={sel.amount}
                                onChange={(e) =>
                                  updateSelection(r.DocEntry, { amount: Number(e.target.value) })
                                }
                                style={{ width: "95%", textAlign: "right" }}
                              />
                            ) : (
                              <span style={{ color: "#aaa" }}>—</span>
                            )}
                          </td>
                          <td style={cellStyle}>
                            {sel ? (
                              <input
                                type="date"
                                value={sel.dueDate}
                                onChange={(e) => updateSelection(r.DocEntry, { dueDate: e.target.value })}
                                style={{ width: "95%" }}
                              />
                            ) : (
                              <span style={{ color: "#aaa" }}>—</span>
                            )}
                          </td>
                          <td style={cellStyle}>
                            {sel ? (
                              <input
                                type="text"
                                value={sel.remittance}
                                maxLength={140}
                                onChange={(e) => updateSelection(r.DocEntry, { remittance: e.target.value })}
                                style={{ width: "95%" }}
                              />
                            ) : (
                              <span style={{ color: "#aaa" }}>—</span>
                            )}
                          </td>
                        </tr>
                      );
                    }),
                  )}
                </tbody>
              </table>
            </div>
          </>
        )}
      </section>
      )}

      {activeTab === "calendario" && (
        <DuesCalendar
          companyId={selectedCompany}
          companyAccounts={companyAccounts}
          batchesVersion={batches.length + batches.reduce((s, b) => s + b.updatedAt.length, 0)}
          onChange={reloadBatches}
        />
      )}

      {activeTab === "pendientes" && (
        <ConfirmingHistory
          batches={batches}
          companyAccounts={companyAccounts}
          onChange={reloadBatches}
        />
      )}
    </>
  );
}

/**
 * Calendario de vencimientos AGREGADO cross-batch. Agrupa por fecha todos
 * los efectos en estado 'posted' (creados en SAP pero aún no liquidados)
 * de TODOS los batches de la empresa, para que el usuario vea de un
 * vistazo cuánto le cargará el banco cada día sin abrir batch por batch.
 *
 * El botón "💰 Pagar" ejecuta UN SOLO JournalEntry consolidado (N líneas
 * DEBE 401 con ShortName=CardCode + 1 línea HABER 572 por el total) —
 * así el asiento cuadra 1:1 con el cargo del banco.
 */
function DuesCalendar({
  companyId,
  companyAccounts,
  batchesVersion,
  onChange,
}: {
  companyId: string;
  companyAccounts: Account[];
  /** Cambia cuando los batches se actualizan (nuevo, contabilizado, etc.)
   *  para forzar refresh del calendario. */
  batchesVersion: number;
  onChange: () => Promise<void>;
}) {
  const [items, setItems] = useState<ConfirmingDueGroup[] | null>(null);
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState<string | null>(null);
  const [msg, setMsg] = useState<string | null>(null);
  const [expandedDate, setExpandedDate] = useState<string | null>(null);
  const [payFromIban, setPayFromIban] = useState<string>("");
  const [payDate, setPayDate] = useState<string>(() => new Date().toISOString().slice(0, 10));

  // Cargar calendario al cambiar de empresa o al actualizar batches.
  // Función extraída para poder llamarla desde el componente hijo
  // BoeAccountAssigner cuando cambia una asignación de cuenta.
  const loadCalendar = async () => {
    if (!companyId) {
      setItems(null);
      return;
    }
    try {
      const r = await fetchConfirmingDuesCalendar(companyId);
      setItems(r.items);
    } catch (e: unknown) {
      setErr(e instanceof Error ? e.message : String(e));
    }
  };
  useEffect(() => {
    void loadCalendar();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [companyId, batchesVersion]);

  // Preseleccionar cuenta de cargo (primera de la empresa con G/L SAP).
  useEffect(() => {
    if (payFromIban) return;
    const first = companyAccounts.find((a) => a.sapGLAccount);
    if (first) setPayFromIban(first.iban);
  }, [companyAccounts, payFromIban]);

  /**
   * Liquida los efectos de una fila del calendario. La fila indica:
   *   - `groupIban`: IBAN del banco emisor conocido (si viene de batches).
   *     Cuando existe, se usa automáticamente sin pedir al usuario.
   *   - Cuando NO existe (pagarés o efectos SAP directos), se usa el IBAN
   *     seleccionado en el header (`payFromIban`).
   */
  const doSettle = async (
    group: ConfirmingDueGroup,
  ) => {
    const effectiveIban = group.senderIban ?? payFromIban;
    if (!effectiveIban) {
      setErr(
        `Los efectos de esta fila (${group.paymentMethod}, venc ${group.dueDate}) no vienen de la app y no tienen banco emisor conocido. Selecciona una "Cta cargo" en la parte superior antes de pagar.`,
      );
      return;
    }
    const bankLabel = group.senderIban
      ? `${group.senderAccountAlias || group.senderIban} (deducido del batch)`
      : `${effectiveIban} (elegido a mano)`;
    if (
      !confirm(
        `¿Liquidar ${group.lineCount} efecto(s) por ${eur(group.totalAmount)}?\n\n` +
          `Vencimiento: ${group.dueDate}\n` +
          `Método: ${group.paymentMethod}\n` +
          `Cta cargo: ${bankLabel}\n\n` +
          `Se creará UN JournalEntry consolidado en SAP:\n` +
          `  DEBE ${group.lineCount} líneas (ShortName por proveedor)\n` +
          `  HABER 1 línea 572 por ${eur(group.totalAmount)}\n\n` +
          `Y se marcarán todos los efectos como pagados (OBOE.BoeStatus='P').`,
      )
    ) {
      return;
    }
    setBusy(true);
    setErr(null);
    setMsg(null);
    try {
      const r = await settleConfirmingByKeys({
        companyId,
        boeKeys: group.boeKeys,
        paidFromIban: effectiveIban,
        paidDate: payDate,
      });
      const warnings = r.results
        .filter((x) => x.warning)
        .map((x) => `  • Ef ${x.sapBoeNum || x.sapBoeKey} · ${x.cardCode}: ${x.warning}`)
        .join("\n");
      setMsg(
        `✓ Asiento SAP creado (JdtNum ${r.sapJdtNum}). ${r.lineCount} efecto(s) liquidados por ${eur(r.totalAmount)}.` +
          (warnings ? `\nWarnings:\n${warnings}` : ""),
      );
      await onChange();
    } catch (e: unknown) {
      setErr(e instanceof Error ? e.message : String(e));
    } finally {
      setBusy(false);
    }
  };

  /**
   * Clasificación por tipo del método de pago (case insensitive):
   *   "confirming" → contiene "confirm"
   *   "pagare"     → contiene "pagar" o "pagaré"
   *   "otros"      → resto (transferencia, cheque, efectivo, sin método)
   */
  const partitioned = useMemo(() => {
    const buckets: Record<
      "confirming" | "pagare" | "otros",
      { items: ConfirmingDueGroup[]; total: number }
    > = {
      confirming: { items: [], total: 0 },
      pagare: { items: [], total: 0 },
      otros: { items: [], total: 0 },
    };
    for (const g of items ?? []) {
      const m = (g.paymentMethod || "").toLowerCase();
      const k: "confirming" | "pagare" | "otros" = m.includes("confirm")
        ? "confirming"
        : m.includes("pagar") || m.includes("pagaré") || m.includes("pagare")
          ? "pagare"
          : "otros";
      buckets[k].items.push(g);
      buckets[k].total = Math.round((buckets[k].total + g.totalAmount) * 100) / 100;
    }
    return buckets;
  }, [items]);

  return (
    <section className="card" style={{ padding: "12px 14px" }}>
      <div className="card-head" style={{ marginBottom: 8 }}>
        <div>
          <h3 style={{ margin: 0, fontSize: "0.95rem" }}>Calendario de vencimientos</h3>
          <p className="meta" style={{ margin: "4px 0 0 0", fontSize: "0.85em" }}>
            Efectos a pagar abiertos en OBOE agrupados por <strong>fecha × método × banco emisor</strong>.
            El banco viene rellenado de los ficheros de la app; para pagarés y efectos SAP directos,
            elige la cta cargo arriba antes de pulsar Pagar.
          </p>
        </div>
        <div style={{ display: "flex", gap: 8, alignItems: "center", flexWrap: "wrap" }}>
          <label style={{ fontSize: "0.9em" }}>
            Cta cargo:{" "}
            <select
              value={payFromIban}
              onChange={(e) => setPayFromIban(e.target.value)}
              disabled={busy || companyAccounts.length === 0}
            >
              <option value="">— selecciona —</option>
              {companyAccounts.map((a) => (
                <option key={a.iban} value={a.iban} disabled={!a.sapGLAccount}>
                  {a.alias || a.iban}
                  {a.sapGLAccount ? ` · G/L ${a.sapGLAccount}` : " · SIN G/L"}
                </option>
              ))}
            </select>
          </label>
          <label style={{ fontSize: "0.9em" }}>
            Fecha pago:{" "}
            <input
              type="date"
              value={payDate}
              onChange={(e) => setPayDate(e.target.value)}
              disabled={busy}
            />
          </label>
        </div>
      </div>

      {err && <p className="error">{err}</p>}
      {msg && (
        <pre
          style={{
            whiteSpace: "pre-wrap",
            background: "rgba(80,200,120,0.08)",
            padding: 8,
            borderRadius: 4,
          }}
        >
          {msg}
        </pre>
      )}

      {items && items.length === 0 && (
        <p className="meta">
          ✓ No hay efectos pendientes de liquidar. Todos los efectos creados están ya pagados
          o no hay batches en estado "Efectos creados".
        </p>
      )}

      {/**
       * Función local: renderiza la tabla del calendario con los grupos
       * indicados. Se llama una vez por sección (confirming, pagaré, otros)
       * para separar visualmente los tipos con títulos y subtotales propios.
       */}
      {(() => {
        const renderCalendarTable = (groupsToShow: ConfirmingDueGroup[]): JSX.Element => (
        <table className="data" style={{ whiteSpace: "nowrap", width: "100%" }}>
          <thead>
            <tr>
              <th>Vencimiento</th>
              <th>Método</th>
              <th>Banco emisor</th>
              <th style={{ textAlign: "right" }}>Efectos</th>
              <th style={{ textAlign: "right" }}>Importe total</th>
              <th>Acciones</th>
            </tr>
          </thead>
          <tbody>
            {groupsToShow.map((g) => {
              const overdue = g.dueDate < new Date().toISOString().slice(0, 10);
              const rowKey = `${g.dueDate}|${g.paymentMethod}|${g.senderIban ?? ""}`;
              const isExpanded = expandedDate === rowKey;
              // Sin banco conocido → el usuario necesita elegir cta cargo
              // antes de poder liquidar (típico de pagarés / SAP directo).
              const needsManualBank = g.senderIban == null;
              const canPay = needsManualBank ? Boolean(payFromIban) : true;
              return (
                <Fragment key={rowKey}>
                  <tr style={{ background: overdue ? "rgba(255,180,50,0.06)" : undefined }}>
                    <td style={{ fontWeight: 600 }}>
                      {g.dueDate}
                      {overdue && (
                        <span style={{ marginLeft: 8, color: "#a60", fontSize: "0.85em" }}>
                          ⚠ vencido
                        </span>
                      )}
                    </td>
                    <td>
                      <span
                        style={{
                          padding: "2px 8px",
                          borderRadius: 8,
                          background: /confirm/i.test(g.paymentMethod)
                            ? "rgba(80,140,220,0.15)"
                            : /pagar[eé]/i.test(g.paymentMethod)
                              ? "rgba(180,120,220,0.15)"
                              : "rgba(150,150,150,0.12)",
                          color: /confirm/i.test(g.paymentMethod)
                            ? "#28b"
                            : /pagar[eé]/i.test(g.paymentMethod)
                              ? "#849"
                              : "#666",
                          fontSize: "0.85em",
                          fontWeight: 500,
                        }}
                      >
                        {g.paymentMethod}
                      </span>
                    </td>
                    <td style={{ fontSize: "0.9em" }}>
                      {g.senderIban ? (
                        <span title={g.senderIban}>
                          {g.senderAccountAlias || g.senderIban}
                          {g.lines.every((l) => l.bankSource === "opym") && (
                            <span
                              title="Cta deducida automáticamente por OPYM del método"
                              style={{
                                marginLeft: 6,
                                padding: "1px 5px",
                                borderRadius: 6,
                                background: "rgba(80,140,220,0.15)",
                                color: "#28b",
                                fontSize: "0.75em",
                                fontWeight: 500,
                              }}
                            >
                              auto
                            </span>
                          )}
                        </span>
                      ) : (
                        <span
                          style={{ color: "#a60", fontSize: "0.85em" }}
                          title="Sin banco emisor conocido — elige 'Cta cargo' arriba antes de pagar"
                        >
                          — sin asignar
                        </span>
                      )}
                    </td>
                    <td style={{ textAlign: "right" }}>
                      {g.lineCount}
                      {(g.appCount > 0 || g.sapCount > 0) && (
                        <span style={{ marginLeft: 6, fontSize: "0.8em", color: "#888" }}>
                          ({g.appCount > 0 && <span style={{ color: "#282" }}>{g.appCount}a</span>}
                          {g.appCount > 0 && g.sapCount > 0 && "·"}
                          {g.sapCount > 0 && <span style={{ color: "#28b" }}>{g.sapCount}s</span>})
                        </span>
                      )}
                    </td>
                    <td style={{ textAlign: "right", fontWeight: 600 }}>{eur(g.totalAmount)}</td>
                    <td style={{ whiteSpace: "nowrap" }}>
                      <button
                        type="button"
                        onClick={() => setExpandedDate(isExpanded ? null : rowKey)}
                        disabled={busy}
                      >
                        {isExpanded ? "▲" : "▼"}
                      </button>{" "}
                      <button
                        type="button"
                        onClick={() => doSettle(g)}
                        disabled={busy || !canPay}
                        title={
                          needsManualBank && !payFromIban
                            ? "Selecciona 'Cta cargo' arriba"
                            : undefined
                        }
                      >
                        💰 Pagar {eur(g.totalAmount)}
                      </button>
                    </td>
                  </tr>
                  {isExpanded && (
                    <tr>
                      <td colSpan={6} style={{ background: "rgba(120,150,200,0.05)", padding: 12 }}>
                        <table className="data compact" style={{ whiteSpace: "nowrap" }}>
                          <thead>
                            <tr>
                              <th>Origen</th>
                              <th>Proveedor</th>
                              <th>Efecto SAP</th>
                              <th>Nº fra SAP</th>
                              <th>Ref. proveedor</th>
                              <th>Fichero (si de la app)</th>
                              <th style={{ textAlign: "right" }}>Importe</th>
                              <th>Cuenta asignada</th>
                            </tr>
                          </thead>
                          <tbody>
                            {g.lines.map((l) => (
                              <tr key={`${l.sapBoeKey}-${l.lineId ?? "sap"}`}>
                                <td>
                                  {l.origin === "app" ? (
                                    <span
                                      style={{
                                        padding: "1px 6px",
                                        borderRadius: 8,
                                        background: "rgba(80,200,120,0.15)",
                                        color: "#282",
                                        fontSize: "0.85em",
                                      }}
                                    >
                                      app
                                    </span>
                                  ) : (
                                    <span
                                      style={{
                                        padding: "1px 6px",
                                        borderRadius: 8,
                                        background: "rgba(80,140,220,0.15)",
                                        color: "#28b",
                                        fontSize: "0.85em",
                                      }}
                                    >
                                      SAP
                                    </span>
                                  )}
                                </td>
                                <td>
                                  {l.cardName}{" "}
                                  <span style={{ fontSize: "0.8em", color: "#888" }}>
                                    ({l.cardCode})
                                  </span>
                                </td>
                                <td>
                                  {l.sapBoeNum
                                    ? `Nº ${l.sapBoeNum}`
                                    : `Key ${l.sapBoeKey}`}
                                </td>
                                <td>{l.invoiceDocNum ?? "—"}</td>
                                <td>{l.invoiceNumAtCard || "—"}</td>
                                <td style={{ fontFamily: "monospace", fontSize: "0.85em" }}>
                                  {l.batchFilename || <span style={{ color: "#aaa" }}>—</span>}
                                </td>
                                <td style={{ textAlign: "right" }}>{eur(l.amount)}</td>
                                <td>
                                  <BoeAccountAssigner
                                    line={l}
                                    companyId={companyId}
                                    companyAccounts={companyAccounts}
                                    onChanged={loadCalendar}
                                    disabled={busy}
                                  />
                                </td>
                              </tr>
                            ))}
                          </tbody>
                        </table>
                      </td>
                    </tr>
                  )}
                </Fragment>
              );
            })}
          </tbody>
        </table>
        );

        // Sección helper: título + subtotal + tabla. Devuelve null si el
        // bucket está vacío para no renderizar secciones sin contenido.
        const renderSection = (
          bucket: { items: ConfirmingDueGroup[]; total: number },
          label: string,
          accent: string,
        ): JSX.Element | null => {
          if (bucket.items.length === 0) return null;
          return (
            <div style={{ marginTop: 12 }}>
              <div
                style={{
                  display: "flex",
                  alignItems: "baseline",
                  justifyContent: "space-between",
                  padding: "4px 2px",
                  borderBottom: `2px solid ${accent}`,
                  marginBottom: 4,
                }}
              >
                <h4 style={{ margin: 0, fontSize: "0.9rem", color: accent }}>
                  {label}{" "}
                  <span style={{ color: "#888", fontWeight: 400 }}>
                    · {bucket.items.length} venc.
                  </span>
                </h4>
                <span style={{ fontSize: "0.9em", color: "#555" }}>
                  Subtotal <strong>{eur(bucket.total)}</strong>
                </span>
              </div>
              {renderCalendarTable(bucket.items)}
            </div>
          );
        };

        if (!items || items.length === 0) return null;
        return (
          <>
            {renderSection(partitioned.confirming, "Confirming", "#28b")}
            {renderSection(partitioned.pagare, "Pagarés", "#849")}
            {renderSection(partitioned.otros, "Otros efectos", "#666")}
          </>
        );
      })()}
    </section>
  );
}

function ConfirmingHistory({
  batches,
  companyAccounts,
  onChange,
}: {
  batches: ConfirmingBatch[];
  companyAccounts: Account[];
  onChange: () => Promise<void>;
}) {
  const [expandedId, setExpandedId] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState<string | null>(null);
  const [msg, setMsg] = useState<string | null>(null);

  const doPost = async (id: string) => {
    if (!confirm("¿Crear en SAP los efectos pendientes de este fichero?")) return;
    setBusy(true);
    setErr(null);
    setMsg(null);
    try {
      const r = await postConfirmingBatch(id);
      const fails = r.results
        .filter((x) => !x.ok)
        .map((x) => `  • Fra ${x.invoiceDocNum}: ${x.error}`)
        .join("\n");
      setMsg(
        `Efectos creados: ${r.okCount} OK, ${r.failCount} fallo(s).` + (fails ? `\nErrores:\n${fails}` : ""),
      );
      await onChange();
    } catch (e: unknown) {
      setErr(e instanceof Error ? e.message : String(e));
    } finally {
      setBusy(false);
    }
  };

  const doCancel = async (id: string) => {
    if (!confirm("¿Anular este fichero? Solo se puede si no hay efectos ya creados en SAP.")) return;
    setBusy(true);
    setErr(null);
    try {
      await cancelConfirmingBatchApi(id);
      await onChange();
    } catch (e: unknown) {
      setErr(e instanceof Error ? e.message : String(e));
    } finally {
      setBusy(false);
    }
  };

  const doDelete = async (id: string) => {
    if (!confirm("¿Borrar el fichero del histórico?")) return;
    setBusy(true);
    setErr(null);
    try {
      await deleteConfirmingBatchApi(id);
      await onChange();
    } catch (e: unknown) {
      setErr(e instanceof Error ? e.message : String(e));
    } finally {
      setBusy(false);
    }
  };

  return (
    <section className="card" style={{ padding: "12px 14px" }}>
      <div className="card-head" style={{ marginBottom: 8 }}>
        <div>
          <h3 style={{ margin: 0, fontSize: "0.95rem" }}>Ficheros pendientes de contabilizar</h3>
          <p className="meta" style={{ margin: "4px 0 0 0", fontSize: "0.85em" }}>
            Ficheros generados. Descarga (XLS Santander / TXT CSB68 / XML SEPA),
            contabiliza los efectos en SAP y liquídalos desde el <em>Calendario de pagos</em>.
          </p>
        </div>
      </div>
      {err && <p className="error">{err}</p>}
      {msg && (
        <pre
          style={{
            whiteSpace: "pre-wrap",
            background: "rgba(80,160,255,0.06)",
            padding: 8,
            borderRadius: 4,
          }}
        >
          {msg}
        </pre>
      )}
      {batches.length === 0 && <p className="meta">Aún no hay ficheros generados.</p>}
      {batches.length > 0 && (
        <div style={{ overflowX: "auto", width: "100%" }}>
          <table className="data" style={{ whiteSpace: "nowrap", width: "100%" }}>
            <thead>
              <tr>
                <th>Fecha</th>
                <th>Fichero</th>
                <th>Cuenta emisora</th>
                <th style={{ textAlign: "right" }}>Total</th>
                <th style={{ textAlign: "right" }}>Efectos</th>
                <th>Estado</th>
                <th>Acciones</th>
              </tr>
            </thead>
            <tbody>
              {batches.map((b) => {
                const posted = b.lines.filter((l) => l.status === "posted" || l.status === "paid").length;
                const paid = b.lines.filter((l) => l.status === "paid").length;
                const failed = b.lines.filter((l) => l.status === "failed").length;
                const pending = b.lines.filter((l) => l.status === "pending").length;
                const canPost = b.status !== "cancelled" && (pending > 0 || failed > 0);
                const canCancel = b.status === "generated" && posted === 0;
                const canDelete = posted === 0;
                return (
                  <Fragment key={b.id}>
                    <tr>
                      <td>{b.createdAt.slice(0, 16).replace("T", " ")}</td>
                      <td style={{ fontFamily: "monospace", fontSize: "0.85em" }}>{b.filename}</td>
                      <td>
                        {b.senderAccountAlias}{" "}
                        <span style={{ fontSize: "0.8em", color: "#888" }}>({formatIban(b.senderIban)})</span>
                      </td>
                      <td style={{ textAlign: "right" }}>{eur(b.totalAmount)}</td>
                      <td style={{ textAlign: "right" }}>
                        {b.lines.length}{" "}
                        <span style={{ color: "#888" }}>({paid}💰 / {posted - paid}✓ / {failed}✗ / {pending}⏳)</span>
                      </td>
                      <td>{statusBadge(b.status)}</td>
                      <td style={{ whiteSpace: "nowrap" }}>
                        <a href={confirmingBatchFileUrl(b.id, "xls")} target="_blank" rel="noreferrer">
                          <button
                            type="button"
                            disabled={busy}
                            title="Descarga la plantilla Excel oficial de Santander (SFC 5.3ext). Al subirlo, MARCA el checkbox 'He utilizado una plantilla Excel del Banco Santander'."
                          >
                            ⬇ XLS (Santander)
                          </button>
                        </a>{" "}
                        <a href={confirmingBatchFileUrl(b.id, "csb68")} target="_blank" rel="noreferrer">
                          <button
                            type="button"
                            disabled={busy}
                            title="Fichero Cuaderno 68 AEB (.txt), estándar oficial español."
                          >
                            ⬇ TXT (68)
                          </button>
                        </a>{" "}
                        <a href={confirmingBatchFileUrl(b.id, "sepa")} target="_blank" rel="noreferrer">
                          <button
                            type="button"
                            disabled={busy}
                            title="Fichero SEPA pain.001 con CtgyPurp=SUPP."
                          >
                            ⬇ XML (SEPA)
                          </button>
                        </a>{" "}
                        <button
                          type="button"
                          onClick={() => setExpandedId(expandedId === b.id ? null : b.id)}
                          disabled={busy}
                        >
                          {expandedId === b.id ? "▲ Cerrar" : "▼ Detalle"}
                        </button>{" "}
                        {canPost && (
                          <button type="button" onClick={() => doPost(b.id)} disabled={busy}>
                            Contabilizar efectos
                          </button>
                        )}{" "}
                        {canCancel && (
                          <button type="button" onClick={() => doCancel(b.id)} disabled={busy}>
                            Anular
                          </button>
                        )}{" "}
                        {canDelete && (
                          <button
                            type="button"
                            className="danger"
                            onClick={() => doDelete(b.id)}
                            disabled={busy}
                          >
                            Borrar
                          </button>
                        )}
                      </td>
                    </tr>
                    {expandedId === b.id && (
                      <tr>
                        <td colSpan={7} style={{ background: "rgba(120,150,200,0.05)" }}>
                          <ConfirmingBatchDetail
                            batch={b}
                            companyAccounts={companyAccounts}
                            onChange={onChange}
                          />
                        </td>
                      </tr>
                    )}
                  </Fragment>
                );
              })}
            </tbody>
          </table>
        </div>
      )}
    </section>
  );
}

function ConfirmingBatchDetail({
  batch,
  companyAccounts,
  onChange,
}: {
  batch: ConfirmingBatch;
  companyAccounts: Account[];
  onChange: () => Promise<void>;
}) {
  const [busy, setBusy] = useState(false);
  const [msg, setMsg] = useState<string | null>(null);
  const [err, setErr] = useState<string | null>(null);
  const [payFromIban, setPayFromIban] = useState<string>(batch.senderIban);
  const [payDate, setPayDate] = useState<string>(() => new Date().toISOString().slice(0, 10));

  /** Agrupa las líneas en estado 'posted' por dueDate para propuesta de pago
   *  al vencimiento (una fila por fecha con importe total). */
  const postedByDueDate = useMemo(() => {
    const map = new Map<string, { dueDate: string; total: number; lines: ConfirmingBatchLine[] }>();
    for (const l of batch.lines) {
      if (l.status !== "posted") continue;
      const cur = map.get(l.dueDate);
      if (cur) {
        cur.total += l.amount;
        cur.lines.push(l);
      } else {
        map.set(l.dueDate, { dueDate: l.dueDate, total: l.amount, lines: [l] });
      }
    }
    return Array.from(map.values()).sort((a, b) => (a.dueDate < b.dueDate ? -1 : 1));
  }, [batch.lines]);

  const doSettle = async (lineIds: string[]) => {
    if (lineIds.length === 0) return;
    setBusy(true);
    setMsg(null);
    setErr(null);
    try {
      const r = await settleConfirmingBatch(batch.id, {
        lineIds,
        paidFromIban: payFromIban,
        paidDate: payDate,
      });
      const fails = r.results
        .filter((x) => !x.ok)
        .map((x) => `  • Fra ${x.invoiceDocNum}: ${x.error}`)
        .join("\n");
      setMsg(
        `Pago efectos: ${r.okCount} OK, ${r.failCount} fallo(s).` + (fails ? `\nErrores:\n${fails}` : ""),
      );
      await onChange();
    } catch (e: unknown) {
      setErr(e instanceof Error ? e.message : String(e));
    } finally {
      setBusy(false);
    }
  };

  return (
    <div style={{ padding: 12 }}>
      <p className="meta" style={{ margin: "0 0 8px 0" }}>
        Presentación <code>{batch.presentationId}</code> · Generado por {batch.createdBy}
        {" · "}Actualizado {batch.updatedAt.slice(0, 16).replace("T", " ")}
      </p>

      {postedByDueDate.length > 0 && (
        <div
          style={{
            background: "rgba(80,160,255,0.06)",
            padding: 10,
            borderRadius: 4,
            margin: "8px 0",
          }}
        >
          <strong>Efectos pendientes de pago al vencimiento:</strong>
          <div style={{ display: "flex", gap: 12, alignItems: "center", marginTop: 8, flexWrap: "wrap" }}>
            <label>
              Cta cargo:{" "}
              <select
                value={payFromIban}
                onChange={(e) => setPayFromIban(e.target.value)}
                disabled={busy}
              >
                {companyAccounts.map((a) => (
                  <option key={a.iban} value={a.iban} disabled={!a.sapGLAccount}>
                    {a.alias || a.iban}
                    {a.sapGLAccount ? ` · G/L ${a.sapGLAccount}` : " · SIN G/L"}
                  </option>
                ))}
              </select>
            </label>
            <label>
              Fecha pago:{" "}
              <input
                type="date"
                value={payDate}
                onChange={(e) => setPayDate(e.target.value)}
                disabled={busy}
              />
            </label>
          </div>
          <table className="data compact" style={{ marginTop: 8, whiteSpace: "nowrap" }}>
            <thead>
              <tr>
                <th>Vencimiento</th>
                <th style={{ textAlign: "right" }}>Nº efectos</th>
                <th style={{ textAlign: "right" }}>Importe total</th>
                <th>Acción</th>
              </tr>
            </thead>
            <tbody>
              {postedByDueDate.map((g) => (
                <tr key={g.dueDate}>
                  <td>{g.dueDate}</td>
                  <td style={{ textAlign: "right" }}>{g.lines.length}</td>
                  <td style={{ textAlign: "right" }}>{eur(g.total)}</td>
                  <td>
                    <button
                      type="button"
                      onClick={() => doSettle(g.lines.map((l) => l.id))}
                      disabled={busy}
                    >
                      💰 Pagar efectos de {g.dueDate} ({eur(g.total)})
                    </button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      {msg && (
        <pre
          style={{
            whiteSpace: "pre-wrap",
            background: "rgba(80,200,120,0.08)",
            padding: 8,
            borderRadius: 4,
          }}
        >
          {msg}
        </pre>
      )}
      {err && <p className="error">{err}</p>}

      <table className="data compact" style={{ whiteSpace: "nowrap", marginTop: 8 }}>
        <thead>
          <tr>
            <th>Proveedor</th>
            <th>Nº fra SAP</th>
            <th>Ref. proveedor</th>
            <th>Venc. efecto</th>
            <th style={{ textAlign: "right" }}>Importe</th>
            <th>Concepto</th>
            <th>Estado</th>
          </tr>
        </thead>
        <tbody>
          {batch.lines.map((l) => (
            <tr key={l.id}>
              <td>
                {l.cardName}{" "}
                <span style={{ fontSize: "0.8em", color: "#888" }}>({l.cardCode})</span>
              </td>
              <td>{l.invoiceDocNum}</td>
              <td>{l.invoiceNumAtCard || "—"}</td>
              <td>{l.dueDate}</td>
              <td style={{ textAlign: "right" }}>{eur(l.amount)}</td>
              <td>{l.remittanceInfo}</td>
              <td>
                {l.status === "paid" && (
                  <span style={{ color: "#3a3" }}>
                    💰 Pagado {l.paidAt} · JE {l.sapPaidJdtNum ?? "?"}
                  </span>
                )}
                {l.status === "posted" && (
                  <span style={{ color: "#28b" }}>
                    ✓ Efecto {l.sapBoeNum ?? l.sapBoeKey ?? "?"}
                  </span>
                )}
                {l.status === "failed" && (
                  <span style={{ color: "#c33" }} title={l.lastError ?? ""}>✗ Error</span>
                )}
                {l.status === "pending" && <span style={{ color: "#888" }}>⏳ Pendiente</span>}
              </td>
            </tr>
          ))}
        </tbody>
      </table>
      {batch.lines.some((l) => l.status === "failed") && (
        <div style={{ marginTop: 8, fontSize: "0.9em" }}>
          <strong>Errores:</strong>
          <ul style={{ margin: "4px 0 0 20px" }}>
            {batch.lines
              .filter((l) => l.status === "failed")
              .map((l) => (
                <li key={l.id}>
                  Fra {l.invoiceDocNum} · {l.cardName}: {l.lastError}
                </li>
              ))}
          </ul>
        </div>
      )}
    </div>
  );
}

/**
 * Selector inline para asignar una cuenta bancaria a un efecto OBOE.
 *
 * Comportamiento según `bankSource` de la línea:
 *   - "batch"  → texto "(batch)" no editable (el banco viene del fichero
 *                de la app y no debería cambiar aquí).
 *   - "opym"   → selector con el IBAN auto-detectado + chip "auto (OPYM)".
 *                Editable: si el usuario elige otro IBAN, se guarda como
 *                assignment manual que sobrescribe el auto.
 *   - "manual" → selector con el IBAN asignado por el usuario. Puede
 *                cambiarlo o volver a "— sin asignar —" para quitar la
 *                asignación (si además el método tiene DflIBAN volverá
 *                al auto).
 *   - "none"   → selector para asignar.
 */
function BoeAccountAssigner({
  line,
  companyId,
  companyAccounts,
  onChanged,
  disabled,
}: {
  line: ConfirmingDueLine;
  companyId: string;
  companyAccounts: Account[];
  onChanged: () => Promise<void>;
  disabled: boolean;
}) {
  const [busy, setBusy] = useState(false);

  if (line.origin === "app") {
    return (
      <span style={{ fontSize: "0.8em", color: "#888" }} title="Fijado por el fichero de la app">
        (batch)
      </span>
    );
  }

  // Valor actual del selector:
  //   - manual: el IBAN asignado por el usuario (visible en el select)
  //   - opym/none: vacío. En opym el chip "auto" al lado explica que
  //     hay una cta preseleccionada por el método. Si el usuario elige
  //     otra aquí, sobrescribe el auto con un assignment manual; si
  //     deja el "— usar auto (OPYM) —", el sistema seguirá usando la
  //     cta del método.
  const currentIban = line.bankSource === "manual" ? line.assignedIban ?? "" : "";

  const setIban = async (iban: string) => {
    setBusy(true);
    try {
      if (iban) {
        await assignConfirmingBoeAccount({ companyId, boeKey: line.sapBoeKey, iban });
      } else {
        await clearConfirmingBoeAccount({ companyId, boeKey: line.sapBoeKey });
      }
      await onChanged();
    } catch (e: unknown) {
      alert(e instanceof Error ? e.message : String(e));
    } finally {
      setBusy(false);
    }
  };

  const isAuto = line.bankSource === "opym";
  return (
    <span style={{ display: "inline-flex", alignItems: "center", gap: 4 }}>
      {isAuto && (
        <span
          title="Cta deducida automáticamente del banco propio configurado en OPYM del método"
          style={{
            padding: "1px 6px",
            borderRadius: 8,
            background: "rgba(80,140,220,0.15)",
            color: "#28b",
            fontSize: "0.75em",
            fontWeight: 500,
          }}
        >
          auto
        </span>
      )}
      <select
        value={currentIban}
        onChange={(e) => void setIban(e.target.value)}
        disabled={disabled || busy}
        style={{ fontSize: "0.85em", maxWidth: 200 }}
        title={
          line.bankSource === "manual"
            ? "Cta asignada manualmente. Cámbiala o pon '— sin asignar —' para quitarla."
            : line.bankSource === "opym"
              ? "Auto-detectada por OPYM. Elige otra aquí para sobrescribir."
              : "Sin cta asignada — elige una para poder liquidar sin elegir cada vez"
        }
      >
        <option value="">
          {isAuto ? "— usar auto (OPYM) —" : "— sin asignar —"}
        </option>
        {companyAccounts.map((a) => (
          <option key={a.iban} value={a.iban} disabled={!a.sapGLAccount}>
            {a.alias || a.iban}
            {a.sapGLAccount ? ` · G/L ${a.sapGLAccount}` : " · SIN G/L"}
          </option>
        ))}
      </select>
    </span>
  );
}

function eur(n: number): string {
  return n.toLocaleString("es-ES", { style: "currency", currency: "EUR" });
}

function formatIban(iban: string): string {
  const s = (iban ?? "").replace(/\s+/g, "").toUpperCase();
  return s.replace(/(.{4})/g, "$1 ").trim();
}

function statusBadge(status: ConfirmingBatch["status"]): JSX.Element {
  const map: Record<ConfirmingBatch["status"], { label: string; color: string; bg: string }> = {
    generated: { label: "Generado", color: "#555", bg: "rgba(120,120,120,0.12)" },
    partially_posted: { label: "Parcial", color: "#a60", bg: "rgba(255,180,50,0.15)" },
    posted: { label: "Efectos creados", color: "#28b", bg: "rgba(80,140,220,0.15)" },
    settled: { label: "Liquidado", color: "#282", bg: "rgba(80,200,120,0.15)" },
    cancelled: { label: "Anulado", color: "#a33", bg: "rgba(200,80,80,0.12)" },
  };
  const s = map[status];
  return (
    <span
      style={{
        display: "inline-block",
        padding: "2px 8px",
        borderRadius: 10,
        fontSize: "0.85em",
        color: s.color,
        background: s.bg,
      }}
    >
      {s.label}
    </span>
  );
}
